import React, {ChangeEvent, useState} from "react";
import {useTranslation} from "react-i18next";
import Modal from "../../../../../../components/modal/Modal";
import ModalTitle from "../../../../../../components/modal/ModalTitle";
import ModalButtons from "../../../../../../components/modal/ModalButtons";
import Button from "../../../../../../components/ui/Button";
import {useKeyboardOnModal} from "../../hooks/useKeyboardOnModal";
import "./placeMultiplesModal.scss";

type PlaceMultiplesModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (quantity: number) => void;
}

export function PlaceMultiplesModal({isOpen, onClose, onConfirm}: PlaceMultiplesModalProps) {
	const {t} = useTranslation();
	const [displayValue, setDisplayValue] = useState("1");
	const quantity = Number(displayValue) || 0;

	useKeyboardOnModal({
		when: isOpen && quantity >= 1,
		keyUpMap: {
			"Enter": handleConfirm,
			"13": handleConfirm
		}
	});

	function handleConfirm() {
		if (quantity < 1) return;
		onConfirm(quantity);
		setDisplayValue("1");
	}

	function handleClose() {
		setDisplayValue("1");
		onClose();
	}

	function handleChange(evt: ChangeEvent<HTMLInputElement>) {
		const value = evt.target.value;
		// Allow empty string for clearing
		if (value === "") {
			setDisplayValue("");
			return;
		}
		// Only accept positive integers (digits only, no decimals, no letters)
		if (!/^\d+$/.test(value)) {
			return;
		}
		const numValue = Number(value);
		// Enforce max of 99
		if (numValue > 99) {
			setDisplayValue("99");
			return;
		}
		setDisplayValue(value);
	}

	function handleBlur() {
		// On blur, ensure minimum of 1
		const numValue = Number(displayValue);
		if (!numValue || numValue < 1) {
			setDisplayValue("1");
		}
	}

	return isOpen ? (
		<Modal isOpen={true} onClose={handleClose} customClass={"place-multiples-modal"}>
			<ModalTitle>{t("editor.count.placeMultiplesModal.title")}</ModalTitle>
			<div className={"place-multiples-modal-content"}>
				<div className={"place-multiples-modal-content_field"}>
					<label className="place-multiples-modal-label">
						<span>{t("editor.count.placeMultiplesModal.howMany")}</span>
						<input
							type="text"
							value={displayValue}
							onChange={handleChange}
							onBlur={handleBlur}
							onKeyUp={(e) => e.stopPropagation()}
							onFocus={(e) => e.target.select()}
							autoFocus
						/>
					</label>
				</div>
			</div>
			<ModalButtons>
				<Button
					label={t("common.buttons.cancel")}
					variant={"optional"}
					onClick={handleClose}
				/>
				<Button
					label={t("editor.count.placeMultiplesModal.place")}
					variant={"primary"}
					onClick={handleConfirm}
					disabled={quantity < 1}
				/>
			</ModalButtons>
		</Modal>
	) : null;
}
